import React from "react";
import { MapPin, Phone, Mail, Clock, Loader, Send, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const ContactForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="5149" className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-sm bg-primary/10 flex items-center justify-center text-primary">
          <Send className="size-8" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold tracking-tight text-foreground">Inquiry Received</h3>
          <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-sm border-primary text-primary hover:bg-primary/5">
          Submit New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-bold text-foreground text-sm uppercase tracking-wider">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Alexander Sterling" {...field} className="bg-background border-border/40 focus:border-primary focus:ring-0 rounded-sm transition-all duration-300" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-bold text-foreground text-sm uppercase tracking-wider">Professional Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="a.sterling@firm.com" {...field} className="bg-background border-border/40 focus:border-primary focus:ring-0 rounded-sm transition-all duration-300" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="company" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-bold text-foreground text-sm uppercase tracking-wider">Firm & Role</FormLabel>
            <FormControl>
              <Input placeholder="Global Equities / Managing Director" {...field} className="bg-background border-border/40 focus:border-primary focus:ring-0 rounded-sm transition-all duration-300" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please describe your inquiry"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-bold text-foreground text-sm uppercase tracking-wider">Inquiry Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly describe your strategic objectives or transformation requirements..." className="min-h-[150px] bg-background border-border/40 focus:border-primary focus:ring-0 rounded-sm transition-all duration-300 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 text-sm text-destructive bg-destructive/5 rounded-sm border border-destructive/20">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold uppercase tracking-widest bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-300 rounded-sm shadow-md">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : <>
            Initiate Conversation <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
};
export default function LetsBuildTheFuture() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="initiate-execution" className="relative py-24 lg:py-32 bg-background text-foreground overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary/3 blur-[120px]" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[60%] rounded-full bg-chart-2/3 blur-[100px]" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div ref={ref} className={`space-y-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-1 rounded-full bg-primary/5 text-primary text-xs font-bold uppercase tracking-widest border border-primary/10">
                <span className="flex size-2 rounded-full bg-primary mr-2 animate-pulse" />
                Contact Portal
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-7xl font-extrabold tracking-tighter text-foreground leading-tight">
                Initiate <span className="text-primary">Execution</span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed max-w-xl font-medium">Quantificial is an AI-first transformation firm helping private equity and enterprise leaders turn data, systems, and operations into measurable performance.<div data-ast-id="f:/src/pages/Contact/sections/LetsBuildTheFuture.tsx|sig:37r3qg" data-ast-start="6508" data-ast-end="6525"><span style={{
                  color: 'rgb(0, 0, 0)',
                  letterSpacing: '-0.32px'
                }}>We deliver across AI, data platforms, automation, enterprise systems, and digital engineering—connecting strategy to execution.</span></div><div data-ast-id="f:/src/pages/Contact/sections/LetsBuildTheFuture.tsx|sig:373kxc" data-ast-start="6663" data-ast-end="6680"><span style={{
                  color: 'rgb(0, 0, 0)',
                  letterSpacing: '-0.32px'
                }}>Through our partnership with Cytrusst, we integrate cybersecurity across governance, cloud, and risk to ensure transformation is secure by design.</span></div><div data-ast-id="f:/src/pages/Contact/sections/LetsBuildTheFuture.tsx|sig:361tqd" data-ast-start="6837" data-ast-end="6854"><span style={{
                  color: 'rgb(0, 0, 0)',
                  letterSpacing: '-0.32px'
                }}>Built for execution. Focused on outcomes.</span></div></p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 size-12 rounded-sm bg-background border border-border/60 shadow-sm flex items-center justify-center text-primary group-hover:border-primary transition-all duration-300">
                  <MapPin className="size-6" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-lg tracking-tight">Executive Briefing Center</h3>
                  <p className="text-muted-foreground mt-1">Global Operations Hub</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 size-12 rounded-sm bg-background border border-border/60 shadow-sm flex items-center justify-center text-primary group-hover:border-primary transition-all duration-300">
                  <Mail className="size-6" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-lg tracking-tight">Direct Inquiry</h3>
                  <Link to="mailto:questions@quantificial.ai" className="text-muted-foreground mt-1 hover:text-primary transition-colors block font-medium">
                    questions@quantificial.ai
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 size-12 rounded-sm bg-background border border-border/60 shadow-sm flex items-center justify-center text-primary group-hover:border-primary transition-all duration-300">
                  <Phone className="size-6" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-lg tracking-tight">Direct Line</h3>
                  <Link to="#" className="text-muted-foreground mt-1 hover:text-primary transition-colors block font-medium">(403) 995 - 7272</Link>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 size-12 rounded-sm bg-background border border-border/60 shadow-sm flex items-center justify-center text-primary group-hover:border-primary transition-all duration-300">
                  <Clock className="size-6" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-lg tracking-tight">Operational Hours</h3>
                  <p className="text-muted-foreground mt-1">Mon-Fri: 08:00 - 18:00 MST</p>
                </div>
              </div>
            </div>
          </div>

          <div className={`relative transition-all duration-1000 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="relative rounded-sm border border-border/40 bg-background shadow-lg p-6 sm:p-8 lg:p-10 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-10 pointer-events-none" />
              
              <div className="relative z-10">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold tracking-tight text-foreground">Initiate Execution Request</h2>
                  <p className="text-muted-foreground mt-2 font-medium">
                    Submit your details below. A transformation partner will respond within one business day.
                  </p>
                </div>

                <FormProvider formKey="initiate_execution_form_contact" formId="initiate_execution_form_contact" sectionName="lets-build-the-future" defaultValues={{
                name: "",
                email: "",
                company: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true
                }
              }} submitText="Initiate Conversation" successMessage="Thank you. Your inquiry has been routed to our executive team.">
                  <ContactForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}